<template>
    <div class="pos-page">
        <div class="pos-tabs">
            <div
                v-for="tab in tabs"
                :key="tab.key"
                class="pos-tab-card"
                :class="[tab.key, { active: activeTab === tab.key }]"
                @click="setActiveTab(tab.key)"
            >
                {{ tab.label }}
            </div>
        </div>

        <div class="pos-tab-body" :class="{ 'pos-billing-body': activeTab === 'pos' || activeTab === 'order' }">
            <ItemCard v-if="activeTab === 'pos' && !loader"
                :parentGroup="parentGroup"
                :contactProfileId="contactProfileId"
                :companyId="companyId"
                v-model:locationId="locationId"
                :locations="locations"
                :productId="productId"
            />

            <BillList v-if="activeTab === 'bill'"/>
            <OrderList v-if="activeTab === 'order_list'"/>

            <OrderCard v-if="activeTab === 'order' && !loader"
                       :parentGroup="parentGroup"
                       :companyId="companyId"
                       v-model:locationId="locationId"
                       :locations="locations"
                       :productId="productId"
            />
        </div>
    </div>
</template>

<script setup>
    import ItemCard from '@/components/molecule/company/pos-invoice/PosBilling.vue';
    import OrderCard from '@/components/molecule/company/pos-invoice/PosOrderBilling.vue';
    import BillList from '@/components/molecule/company/pos-invoice/PosInvoiceList.vue';
    import OrderList from '@/components/molecule/company/pos-invoice/PosOrderList.vue';
    import handleInventory from '@/services/modules/inventory';
    import {onMounted, ref, computed, watch} from 'vue';
    import {useRoute, useRouter} from 'vue-router';
    import handleBusinessesLocations from '@/services/modules/businessesLocations';
    import { useStore } from 'vuex';

    const $route = useRoute();
    const $router = useRouter();
    const $store = useStore();

    const loader = ref(true);
    const contactProfileId = ref(null);
    const companyId = $route.params.companyId
    const companyQuery = `?company_id=${companyId}`;
    const locations = ref([]);
    const locationId = ref(null);
    const productId = ref(null);
    const activeTab = ref('pos');

    const parentGroup = ref([]);

    const { fetchGroupParent } = handleInventory();
    const { fetchBusinessLocationsList } = handleBusinessesLocations();

    const tabs = [
        { key: 'pos', label: 'POS Billing' },
        { key: 'order', label: 'POS Order' },
        { key: 'order_list', label: 'Order List' },
        { key: 'bill', label: 'Bill List' },
    ]

    onMounted(async ()=>{
        if ($route.query.selected_tab) {
            activeTab.value = $route.query.selected_tab;
        }

        loader.value = true;
        await getProductHeadGroup(true);
        await getLocations();
        loader.value = false;
    })

    watch(
        () => $route.query.selected_tab,
        (newTab) => {
          if (!newTab) return

          activeTab.value = newTab

          const newQuery = { ...$route.query }
          let shouldUpdate = false

          if (newTab !== 'pos' && newQuery.sales_order_id) {
            delete newQuery.sales_order_id
            shouldUpdate = true
          }

          if (newTab !== 'order' && newQuery.order_sales_id) {
            delete newQuery.order_sales_id
            shouldUpdate = true
          }

          if (shouldUpdate) {
            $router.replace({ query: newQuery })
          }
        },
        { immediate: true }
    )

    const setActiveTab = (key) => {
        activeTab.value = key;
        $router.replace({
            query: {
                ...$route.query,
                redirect_route: 'pos-invoice',
                selected_tab: key,
            },
        });
    }

    const getLocations = async () => {
        const locationQuery = `${companyQuery}&location_type=fg_warehouse`;
        const res = await fetchBusinessLocationsList(locationQuery);
        if(!res.status) return;
        locations.value = res.data;
        locationId.value = locations.value[0].id;
    }

    const getProductHeadGroup = async () => {
        const query = `?company_id=${companyId}&product_type=finished_goods,services`;

        try{
            const { data } = await fetchGroupParent(query);
            if(data.length == 0){
                return
            }
            parentGroup.value = data;
        }catch(e){
            console.log("Something went wrong");
        }
    }
</script>

<style scoped>
.pos-page {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 80px);
    overflow: hidden;
    padding: 0 10px 10px 10px;
}

.pos-tabs {
    flex: 0 0 auto;
}

.pos-tab-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    margin-top: 10px;
    padding: 12px;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}

.pos-billing-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.pos-billing-body :deep(.grp-pd) {
    flex: 0 0 auto;
}

.pos-billing-body :deep(.content-border) {
    flex: 1 1 auto;
    min-height: 0;
}

.pos-billing-body :deep(.content-height) {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.pos-billing-body :deep(.product-list) {
    height: 100%;
}

.pos-billing-body :deep(.content-border > [class*="col-"]) {
    height: 100%;
}

.font-24 {
    font-size: 24px;
}

.pos-tab {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    font-weight: 500;
    cursor: pointer;
    background: #f5f5f5;
    transition: all 0.2s ease;
}

.pos-tab:last-child {
    border-right: none;
}

.pos-tab.active {
    font-size: 20px;
    font-weight: 700;
    background: #cfe2ff; /* active blue */
}

.pos-tab:not(.active):hover {
    background: #e9ecef;
}

.pos-tabs {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 2px;
    padding: 10px 0 4px 0;
}

/* BASE CARD */
.pos-tab-card {
    display: flex;        
    align-items: center;    
    justify-content: center;

    padding: 14px 10px;
    border-radius: 12px;
    text-align: center;
    font-weight: 600;
    cursor: pointer;

    color: #fff;
    box-shadow: 0 4px 10px rgba(0,0,0,0.12);
    transition: all 0.2s ease;
}

/* ACTIVE */
.pos-tab-card.active {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 20px rgba(0,0,0,0.2);
    font-size: 24px;
}

/* HOVER */
.pos-tab-card:hover {
    transform: translateY(-1px);
}

/* POS Billing → Primary */
.pos-tab-card.pos {
    background: linear-gradient(135deg, #7367f0, #9e95f5);
}

/* Bill List → Success */
.pos-tab-card.bill {
    background: linear-gradient(135deg, #28c76f, #55dd92);
}

/* Order List → Warning */
.pos-tab-card.order {
    background: linear-gradient(135deg, #ff9f43, #ffc085);
}

/* Customer List → Danger */
.pos-tab-card.order_list {
    background: linear-gradient(135deg, #ea5455, #f08182);
}

</style>